import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle } from "vue"

import {computed, onMounted, watch} from 'vue';
import {useRouter} from 'vue-router';
import TopToolbar from './components/Layout/TopToolbar.vue';
import SearchHeader from '@/components/Layout/SearchHeader.vue';
import HomeBranding from './views/HomeComponents/HomeBranding.vue';
import SplashScreen from './views/HomeComponents/SplashScreen.vue';
import PageHeader from '@/components/Layout/PageHeader.vue';

import {useSettingsStore} from '@/api/store/settings';
import {useUserStore} from './api/store/user';

import {appService} from '@/api/services/app';

import {Capacitor} from '@capacitor/core';
import {performImmediateUpdate} from '@/plugins/updater';

import {NativeBiometric} from "capacitor-native-biometric";

import DownloadPopup from '@/components/custom/DownloadPopup.vue';
import {setUser} from "@/api/services/auth";
import GlobalSnackbar from "@/components/GlobalSnackbar.vue";
import {hexToRgba} from "@/tools/design";


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const router = useRouter();

const performBiometricVerification = async () => {
  const result = await NativeBiometric.isAvailable();

  if (!result.isAvailable) return;

  const verified = await NativeBiometric.verifyIdentity({
    reason: "Secure Login",
    title: "Log in",
    subtitle: "We just need to verify your identity.",
    description: "Please use your biometric authentication method to log in.",
  })
      .then(() => true)
      .catch(() => false);

  if (!verified) return false;

  return true;
}

const settingsStore = useSettingsStore();
const userStore = useUserStore();

const appStart = async () => {

  console.log("App Starting");

  if (Capacitor.getPlatform() == "web") {
    console.log("Running on Web Mode");
  } else {
    console.log("Running on Mobile");
    console.log("Checking for updates");
    performImmediateUpdate();
  }

  // Skip fetching if settings are already loaded (e.g., from login)
  if (settingsStore.set) {
    console.log("Settings already loaded, skipping fetch");
    updateCSSVariables();
    return;
  }

  if (userStore.logged_in && (Capacitor.getPlatform() == "web" || await performBiometricVerification())) {
    console.log("User Already Logged In");
    console.log("Fetching App Configuration");

    await appService.fetchDisplay().then((response: any) => {

      userStore.setUser(response.client, response.clinicAvatar);
      setUser(response.client);

      if (!response.app.settings || response.app.settings == "null") {
        settingsStore.set = true; // Set even if no settings
        return;
      }

      const data = JSON.parse(response.app.settings);

      if (response.app.image && response.app.image.file && response.app.image.file.file_path) {
        data.brandingImg = response.app.image.file.file_path;
        //console.log("Branding Image: ", settingsStore.settings.brandingImg);
      }

      // Use store action to update settings (maintains reactivity)
      settingsStore.updateSettings(data);
      updateCSSVariables();

    }).catch((err: any) => {
      console.log("Display Error: ", err);
      if (err.response?.status === 401) {
        router.push({name: 'login'});
      }
      // Set to true even on error so UI can render
      settingsStore.set = true;
    });
  } else {
    // Not logged in, use default settings
    settingsStore.set = true;
  }
};

// Update CSS variables globally based on the settings
const updateCSSVariables = () => {
  const primaryColor = settingsStore.settings.primaryColor;
  const buttonColor = settingsStore.settings.buttonColor;
  const buttonColor2 = settingsStore.settings.buttonColor2;

  document.documentElement.style.setProperty('--primary-color', primaryColor);
  document.documentElement.style.setProperty('--primary-button-color', buttonColor);
  document.documentElement.style.setProperty('--secondary-button-color', buttonColor2);
};

onMounted(() => {
  router.isReady().then(() => {
    if (router.currentRoute.value.name && router.currentRoute.value.name !== 'preview') {
      console.log("Route: ", router.currentRoute.value.name);
      appStart();
    } else {
      console.log("Route: ", router.currentRoute.value.name);
      settingsStore.set = true;
    }
  });
  updateCSSVariables();
});

const bgStyle = computed(() => {
  if (!settingsStore.settings) return 'background-color: rgba(255, 255, 255, 0.3);'; // 30% opacity
  if (router.currentRoute.value.meta.showBrandBar || router.currentRoute.value.meta.withSettingsBackground) {
    return 'background: linear-gradient(180deg, ' + hexToRgba(settingsStore.settings.primaryColor, 0.3) + '0, ' + 'rgba(255, 255, 255, 0.3)' + ' 50% 100%);';
  }
  return 'background-color: rgba(255, 255, 255, 0.3);';
});



watch(() => settingsStore.settings, () => {
  updateCSSVariables();
}, { deep: true })


return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!
  const _component_v_slide_x_transition = _resolveComponent("v-slide-x-transition")!
  const _component_v_main = _resolveComponent("v-main")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_overlay = _resolveComponent("v-overlay")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, {
    style: _normalizeStyle(bgStyle.value),
    class: "mx-0"
  }, {
    default: _withCtx(() => [
      _createVNode(GlobalSnackbar),
      (_unref(settingsStore).set && _unref(userStore).logged_in && _unref(router).currentRoute.value.name != 'preview')
        ? (_openBlock(), _createBlock(TopToolbar, { key: 0 }))
        : _createCommentVNode("", true),
      (_unref(settingsStore).set || _unref(router).currentRoute.value.name == 'login')
        ? (_openBlock(), _createBlock(_component_v_container, {
            key: 1,
            class: _normalizeClass(["pa-0 desktop", { 'top-buffer': _unref(router).currentRoute.value.name != 'login' }])
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_row, { "no-gutters": "" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, {
                    class: "pa-0",
                    cols: "12"
                  }, {
                    default: _withCtx(() => [
                      (_unref(settingsStore).settings.header == 1 && _unref(router).currentRoute.value.name != 'preview')
                        ? (_openBlock(), _createBlock(SearchHeader, { key: 0 }))
                        : _createCommentVNode("", true),
                      (_unref(settingsStore).settings.header == 2 && _unref(router).currentRoute.value.meta.showBrandBar && _unref(router).currentRoute.value.name != 'preview')
                        ? (_openBlock(), _createBlock(HomeBranding, { key: 1 }))
                        : _createCommentVNode("", true),
                      (_unref(router).currentRoute.value.meta.showTitleBar && _unref(router).currentRoute.value.name != 'preview')
                        ? (_openBlock(), _createBlock(PageHeader, {
                            key: 2,
                            class: "mt-2"
                          }))
                        : _createCommentVNode("", true),
                      _createVNode(_component_v_main, { style: {"padding-top":"0px"} }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_slide_x_transition, { mode: "out-in" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_router_view)
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["class"]))
        : _createCommentVNode("", true),
      _createVNode(_component_v_overlay, {
        "model-value": _unref(settingsStore).loading,
        class: "align-center justify-center",
        color: "deep-purple-accent-4"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_progress_circular, {
            color: "primary",
            indeterminate: "",
            size: "64"
          })
        ]),
        _: 1
      }, 8, ["model-value"]),
      _createVNode(_component_v_overlay, {
        "model-value": _unref(settingsStore).error,
        class: "align-center justify-center",
        color: "red"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, { class: "pa-5" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-center" }, {
                default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                  _createTextVNode("Error", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "text-center" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(settingsStore).error_message), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, { class: "justify-center" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(settingsStore).error = false))
                  }, {
                    default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                      _createTextVNode("Close", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["model-value"])
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})